import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Play, Plus, Repeat, Trash2, Zap, FileStack } from "lucide-react";

const fmtCHF = (v) =>
  Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FREQ_LABELS = { monthly: "Monatlich", quarterly: "Quartalsweise", yearly: "Jährlich" };

// ---------- Tab 1: Abgrenzungs-Pläne ----------

function AccrualsTab({ companyId }) {
  const [plans, setPlans] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({
    kind: "ara", label: "", total_amount: "", months: "12",
    start_month: new Date().toISOString().slice(0, 7), pl_account_id: "",
    create_initial_booking: false, source_receipt_id: null,
  });

  const reload = useCallback(async () => {
    const [plansRes, suggRes] = await Promise.all([
      apiClient.get(`/companies/${companyId}/accrual-plans`),
      apiClient.get(`/companies/${companyId}/accrual-suggestions`).catch(() => ({ data: [] })),
    ]);
    setPlans(plansRes.data || []);
    setSuggestions(suggRes.data || []);
  }, [companyId]);

  useEffect(() => { reload().catch(() => {}); }, [reload]);

  const openFromSuggestion = (s) => {
    setForm({
      kind: "ara",
      label: s.label,
      total_amount: String(s.amount),
      months: String(s.suggested_months),
      start_month: s.suggested_start_month,
      pl_account_id: s.pl_account_id || "",
      create_initial_booking: true,
      source_receipt_id: s.receipt_id,
    });
    setShowCreate(true);
  };

  const dismissSuggestion = async (s) => {
    try {
      await apiClient.post(`/companies/${companyId}/accrual-suggestions/${s.receipt_id}/dismiss`);
      setSuggestions((prev) => prev.filter((x) => x.receipt_id !== s.receipt_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const create = async () => {
    try {
      await apiClient.post(`/companies/${companyId}/accrual-plans`, {
        kind: form.kind,
        label: form.label,
        total_amount: parseFloat(form.total_amount),
        months: parseInt(form.months, 10),
        start_month: form.start_month,
        pl_account_id: form.pl_account_id,
        create_initial_booking: form.kind === "ara" ? !!form.create_initial_booking : false,
        source_receipt_id: form.source_receipt_id,
      });
      toast.success(
        form.create_initial_booking && form.kind === "ara"
          ? "Plan erstellt + Bildungs-Buchung (1300) verbucht — Monatstranchen laufen automatisch."
          : "Abgrenzungs-Plan erstellt — fällige Monate werden automatisch gebucht."
      );
      setShowCreate(false);
      setForm({ ...form, label: "", total_amount: "", pl_account_id: "", create_initial_booking: false, source_receipt_id: null });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen");
    }
  };

  const runDue = async () => {
    setRunning(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/accrual-plans/run-due`);
      const n = r.data?.booked || 0;
      toast.success(n > 0 ? `${n} Tranche(n) gebucht ✓` : "Keine fälligen Tranchen.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setRunning(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Plan löschen? Bereits gebuchte Tranchen bleiben im Journal erhalten.")) return;
    await apiClient.delete(`/companies/${companyId}/accrual-plans/${id}`);
    toast.success("Plan gelöscht");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500 max-w-xl">
          Verteilt einen Betrag automatisch über mehrere Monate — z.&nbsp;B. Versicherung CHF 1'200
          → 100/Monat. Fällige Tranchen bucht das System jede Nacht selbst.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runDue} disabled={running} data-testid="accruals-run-due-btn" title="Bucht alle fälligen Abgrenzungs-Tranchen sofort, statt auf den nächtlichen Automatik-Lauf zu warten">
            <Play className="h-3.5 w-3.5 mr-1" /> Fällige jetzt buchen
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="accruals-create-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Neuer Plan
          </Button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50/50" data-testid="accrual-suggestions-card">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-slate-900">
              Erkannte Jahres-/Periodenrechnungen ({suggestions.length})
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Diese gebuchten Belege sehen nach periodenübergreifenden Rechnungen aus.
            Mit einem Klick abgrenzen: Umbuchung auf 1300 + automatische monatliche Auflösung.
          </p>
          <div className="space-y-1.5">
            {suggestions.map((s) => (
              <div
                key={s.receipt_id}
                className="flex items-center justify-between gap-3 rounded-md bg-white border border-amber-100 px-3 py-2"
                data-testid={`accrual-suggestion-${s.receipt_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-slate-900 truncate">{s.label}</div>
                  <div className="text-[11px] text-slate-500">
                    {formatDate(s.date)} · CHF {fmtCHF(s.amount)} · Vorschlag: {s.suggested_months} Monate ab {s.suggested_start_month}
                    {s.pl_account_id && <> · Konto {s.pl_account_id}</>}
                    <span className="ml-1 text-amber-600">(Stichwort: {s.matched_keyword})</span>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => openFromSuggestion(s)} data-testid={`accrual-suggestion-accept-${s.receipt_id}`} title="Verteilt den Aufwand dieser Jahresrechnung auf mehrere Monate (Abgrenzung)">
                    Abgrenzen
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => dismissSuggestion(s)} title="Ausblenden" data-testid={`accrual-suggestion-dismiss-${s.receipt_id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {plans.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="accruals-empty">
          Noch keine Abgrenzungs-Pläne.
        </Card>
      ) : (
        <div className="space-y-2">
          {plans.map((p) => (
            <Card key={p.id} className="p-4" data-testid={`accrual-plan-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{p.label}</span>
                    <Badge variant="outline" className={p.kind === "ara" ? "border-emerald-300 text-emerald-700" : "border-teal-300 text-teal-700"}>
                      {p.kind === "ara" ? "ARA-Auflösung (1300)" : "PRA-Bildung (2300)"}
                    </Badge>
                    {p.status === "completed" && (
                      <Badge className="bg-slate-100 text-slate-600">Abgeschlossen</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    CHF {fmtCHF(p.total_amount)} über {p.months} Monate ab {p.start_month} ·
                    CHF {fmtCHF(p.monthly_amount)}/Monat · Konto {p.pl_account_id} / {p.balance_account_id}
                  </div>
                  <div className="mt-2 h-1.5 w-64 max-w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (p.generated_count / p.months) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {p.generated_count}/{p.months} Tranchen gebucht
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(p.id)} data-testid={`accrual-plan-delete-${p.id}`}>
                  <Trash2 className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="accrual-create-dialog">
          <DialogHeader><DialogTitle>Neuer Abgrenzungs-Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Typ</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="accrual-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ara">ARA auflösen — vorausbezahlter Aufwand (Aufwand / 1300)</SelectItem>
                  <SelectItem value="pra">PRA bilden — Rechnung folgt später (Aufwand / 2300)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Bezeichnung</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="z.B. Betriebshaftpflicht 2026" data-testid="accrual-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Gesamtbetrag CHF</Label>
                <Input type="number" step="0.05" value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  placeholder="1200.00" data-testid="accrual-amount-input" />
              </div>
              <div>
                <Label className="text-sm">Monate</Label>
                <Input type="number" min="2" max="60" value={form.months}
                  onChange={(e) => setForm({ ...form, months: e.target.value })}
                  data-testid="accrual-months-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Start-Monat</Label>
                <Input type="month" value={form.start_month}
                  onChange={(e) => setForm({ ...form, start_month: e.target.value })}
                  data-testid="accrual-start-input" />
              </div>
              <div>
                <Label className="text-sm">Aufwandskonto</Label>
                <Input value={form.pl_account_id}
                  onChange={(e) => setForm({ ...form, pl_account_id: e.target.value })}
                  placeholder="z.B. 6300" data-testid="accrual-account-input" />
              </div>
            </div>
            {form.total_amount && form.months && (
              <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
                → CHF {fmtCHF(parseFloat(form.total_amount || 0) / parseInt(form.months || 1, 10))} pro Monat
              </div>
            )}
            {form.kind === "ara" && (
              <label className="flex items-start gap-2 text-xs cursor-pointer rounded-md border border-slate-200 bg-slate-50/60 p-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!form.create_initial_booking}
                  onChange={(e) => setForm({ ...form, create_initial_booking: e.target.checked })}
                  data-testid="accrual-initial-booking-checkbox"
                />
                <span className="text-slate-700">
                  <span className="font-medium">Bildungs-Buchung sofort erstellen</span> — bucht den vollen Betrag
                  Soll 1300 / Haben {form.pl_account_id || "Aufwandskonto"} (neutralisiert den bereits gebuchten
                  Aufwand; die Monatstranchen lösen dann automatisch auf).
                </span>
              </label>
            )}
            <Button className="w-full" onClick={create}
              disabled={!form.label || !form.total_amount || !form.pl_account_id}
              data-testid="accrual-create-submit">
              Plan erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Tab 2: Wiederkehrende Buchungen ----------

function RecurringTab({ companyId }) {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", amount: "", debit_account: "", credit_account: "",
    frequency: "monthly", day_of_month: "1", start_date: new Date().toISOString().slice(0, 10),
  });

  const reload = useCallback(async () => {
    const r = await apiClient.get(`/companies/${companyId}/recurring-bookings`);
    setItems(r.data || []);
  }, [companyId]);

  useEffect(() => { reload().catch(() => {}); }, [reload]);

  const create = async () => {
    try {
      const amt = parseFloat(form.amount);
      await apiClient.post(`/companies/${companyId}/recurring-bookings`, {
        name: form.name,
        description: form.description || form.name,
        lines: [
          { account_id: form.debit_account, debit: amt, credit: 0 },
          { account_id: form.credit_account, debit: 0, credit: amt },
        ],
        frequency: form.frequency,
        day_of_month: parseInt(form.day_of_month, 10),
        start_date: form.start_date,
      });
      toast.success("Wiederkehrende Buchung angelegt — läuft automatisch.");
      setShowCreate(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen");
    }
  };

  const runDue = async () => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/recurring-bookings/run-due`);
      const n = r.data?.booked || 0;
      toast.success(n > 0 ? `${n} Buchung(en) erzeugt ✓` : "Keine fälligen Buchungen.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const toggle = async (id) => {
    await apiClient.put(`/companies/${companyId}/recurring-bookings/${id}/toggle`);
    reload();
  };

  const remove = async (id) => {
    if (!window.confirm("Wiederkehrende Buchung löschen?")) return;
    await apiClient.delete(`/companies/${companyId}/recurring-bookings/${id}`);
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500 max-w-xl">
          Miete, Leasing, Versicherungen — einmal einrichten, das System bucht automatisch
          monatlich, quartalsweise oder jährlich.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runDue} data-testid="recurring-run-due-btn" title="Bucht alle fälligen wiederkehrenden Buchungen sofort, statt auf den nächtlichen Automatik-Lauf zu warten">
            <Play className="h-3.5 w-3.5 mr-1" /> Fällige jetzt buchen
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="recurring-create-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Neue Buchung
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="recurring-empty">
          Noch keine wiederkehrenden Buchungen.
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const amount = r.lines.reduce((s, l) => s + (l.debit || 0), 0);
            return (
              <Card key={r.id} className="p-4" data-testid={`recurring-booking-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{r.name}</span>
                      <Badge variant="outline">{FREQ_LABELS[r.frequency]}</Badge>
                      <Badge className={r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>
                        {r.active ? "Aktiv" : "Pausiert"}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      CHF {fmtCHF(amount)} · {r.lines.filter(l => l.debit > 0).map(l => l.account_id).join("+")} an{" "}
                      {r.lines.filter(l => l.credit > 0).map(l => l.account_id).join("+")} ·
                      nächster Lauf {r.next_run} · {r.generated_count}× gebucht
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => toggle(r.id)} data-testid={`recurring-toggle-${r.id}`}>
                      {r.active ? "Pausieren" : "Aktivieren"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`recurring-delete-${r.id}`}>
                      <Trash2 className="h-4 w-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="recurring-create-dialog">
          <DialogHeader><DialogTitle>Neue wiederkehrende Buchung</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Büromiete" data-testid="recurring-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm">Betrag CHF</Label>
                <Input type="number" step="0.05" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="2500.00" data-testid="recurring-amount-input" />
              </div>
              <div>
                <Label className="text-sm">Soll-Konto</Label>
                <Input value={form.debit_account}
                  onChange={(e) => setForm({ ...form, debit_account: e.target.value })}
                  placeholder="6000" data-testid="recurring-debit-input" />
              </div>
              <div>
                <Label className="text-sm">Haben-Konto</Label>
                <Input value={form.credit_account}
                  onChange={(e) => setForm({ ...form, credit_account: e.target.value })}
                  placeholder="1020" data-testid="recurring-credit-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm">Intervall</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                    <SelectItem value="quarterly">Quartalsweise</SelectItem>
                    <SelectItem value="yearly">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Tag im Monat</Label>
                <Input type="number" min="1" max="28" value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                  data-testid="recurring-day-input" />
              </div>
              <div>
                <Label className="text-sm">Start</Label>
                <SwissDateField  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="recurring-start-input" />
              </div>
            </div>
            <Button className="w-full" onClick={create}
              disabled={!form.name || !form.amount || !form.debit_account || !form.credit_account}
              data-testid="recurring-create-submit">
              Anlegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Tab 3: Buchungsvorlagen ----------

function TemplatesTab({ companyId }) {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", amount: "", debit_account: "", credit_account: "" });

  const reload = useCallback(async () => {
    const r = await apiClient.get(`/companies/${companyId}/booking-templates`);
    setItems(r.data || []);
  }, [companyId]);

  useEffect(() => { reload().catch(() => {}); }, [reload]);

  const create = async () => {
    try {
      const amt = parseFloat(form.amount);
      await apiClient.post(`/companies/${companyId}/booking-templates`, {
        name: form.name,
        description: form.description || form.name,
        lines: [
          { account_id: form.debit_account, debit: amt, credit: 0 },
          { account_id: form.credit_account, debit: 0, credit: amt },
        ],
      });
      toast.success("Vorlage gespeichert");
      setShowCreate(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen");
    }
  };

  const book = async (tpl) => {
    setBookingId(tpl.id);
    try {
      await apiClient.post(`/companies/${companyId}/booking-templates/${tpl.id}/book`, {});
      toast.success(`«${tpl.name}» gebucht ✓ (heute)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Buchen fehlgeschlagen");
    } finally {
      setBookingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Vorlage löschen?")) return;
    await apiClient.delete(`/companies/${companyId}/booking-templates/${id}`);
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500 max-w-xl">
          Häufige Handbuchungen (Telefon, Internet, Bankspesen) als Vorlage speichern —
          und mit einem Klick buchen.
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="template-create-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Neue Vorlage
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="templates-empty">
          Noch keine Vorlagen.
        </Card>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => {
            const amount = t.lines.reduce((s, l) => s + (l.debit || 0), 0);
            return (
              <Card key={t.id} className="p-4" data-testid={`booking-template-${t.id}`}>
                <div className="font-medium text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  CHF {fmtCHF(amount)} · {t.lines.filter(l => l.debit > 0).map(l => l.account_id).join("+")} an{" "}
                  {t.lines.filter(l => l.credit > 0).map(l => l.account_id).join("+")}
                </div>
                <div className="flex gap-1 mt-3">
                  <Button size="sm" className="flex-1" onClick={() => book(t)}
                    disabled={bookingId === t.id} data-testid={`template-book-${t.id}`}>
                    <Zap className="h-3.5 w-3.5 mr-1" /> Jetzt buchen
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="template-create-dialog">
          <DialogHeader><DialogTitle>Neue Buchungsvorlage</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Swisscom Telefon" data-testid="template-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm">Betrag CHF</Label>
                <Input type="number" step="0.05" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="89.00" data-testid="template-amount-input" />
              </div>
              <div>
                <Label className="text-sm">Soll-Konto</Label>
                <Input value={form.debit_account}
                  onChange={(e) => setForm({ ...form, debit_account: e.target.value })}
                  placeholder="6510" data-testid="template-debit-input" />
              </div>
              <div>
                <Label className="text-sm">Haben-Konto</Label>
                <Input value={form.credit_account}
                  onChange={(e) => setForm({ ...form, credit_account: e.target.value })}
                  placeholder="1020" data-testid="template-credit-input" />
              </div>
            </div>
            <Button className="w-full" onClick={create}
              disabled={!form.name || !form.amount || !form.debit_account || !form.credit_account}
              data-testid="template-create-submit">
              Vorlage speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Page ----------

export default function AutoBookings() {
  const companyId = localStorage.getItem("demoCompanyId");

  return (
    <div className="space-y-6" data-testid="auto-bookings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <CalendarClock className="h-6 w-6 text-violet-600" />
          Automatische Buchungen
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Abgrenzungen über Monate verteilen, wiederkehrende Buchungen und Vorlagen —
          das System bucht jede Nacht automatisch.
        </p>
      </div>

      <Tabs defaultValue="accruals">
        <TabsList>
          <TabsTrigger value="accruals" data-testid="tab-accruals">
            <FileStack className="h-4 w-4 mr-1.5" /> Abgrenzungen
          </TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">
            <Repeat className="h-4 w-4 mr-1.5" /> Wiederkehrende Buchungen
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Zap className="h-4 w-4 mr-1.5" /> Vorlagen
          </TabsTrigger>
        </TabsList>
        <TabsContent value="accruals" className="mt-4">
          <AccrualsTab companyId={companyId} />
        </TabsContent>
        <TabsContent value="recurring" className="mt-4">
          <RecurringTab companyId={companyId} />
        </TabsContent>
        <TabsContent value="templates" className="mt-4">
          <TemplatesTab companyId={companyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
